import React from 'react';
import { useTranslation } from 'react-i18next';
import { Clock, Calendar, Settings, Users, Timer, CheckCircle } from 'lucide-react';

export default function OperationsTab({ formData, onInputChange, onWorkingDayToggle, constants }) {
  const { t } = useTranslation();
  const { DAYS_OF_WEEK } = constants;

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl p-6 border border-purple-100">
        <div className="flex items-center mb-4">
          <div className="p-3 bg-purple-100 rounded-lg mr-4">
            <Clock className="w-6 h-6 text-purple-600" />
          </div>
          <div>
            <h3 className="text-xl font-semibold text-gray-900">{t('settings.tabs.operations')}</h3>
            <p className="text-sm text-gray-600">{t('settings.operations.description')}</p>
          </div>
        </div>
      </div>

      {/* Working Days */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
          <h4 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Calendar className="w-5 h-5" />
            {t('settings.workingDays')}
          </h4>
          <p className="text-sm text-gray-600 mt-1">{t('settings.workingDaysDescription')}</p>
        </div>
        
        <div className="p-6">
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
            {DAYS_OF_WEEK.map((day) => (
              <label key={day} className="cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.workingDays.includes(day)}
                  onChange={() => onWorkingDayToggle(day)}
                  className="sr-only"
                />
                <div className={`
                  p-4 rounded-lg border-2 transition-all duration-200 cursor-pointer text-center hover:shadow-md
                  ${formData.workingDays.includes(day) 
                    ? 'bg-purple-600 text-white border-purple-600 shadow-lg transform scale-105' 
                    : 'bg-white hover:bg-gray-50 border-gray-300 hover:border-purple-300'
                  }
                `}>
                  <span className={`font-medium text-sm ${
                    formData.workingDays.includes(day) ? 'text-white' : 'text-gray-900'
                  }`}>
                    {t(`days.${day}`)}
                  </span>
                  {formData.workingDays.includes(day) && (
                    <CheckCircle className="w-4 h-4 mx-auto mt-1 text-white" />
                  )}
                </div>
              </label>
            ))}
          </div>
        </div>
      </div>

      {/* Operating Hours */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
          <h4 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Timer className="w-5 h-5" />
            {t('settings.operatingHours')}
          </h4>
          <p className="text-sm text-gray-600 mt-1">{t('settings.operatingHoursDescription')}</p>
        </div>
        
        <div className="p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Clock className="w-4 h-4 inline mr-1" />
                {t('settings.openingTime')}
              </label>
              <input
                type="time"
                value={formData.openingTime}
                onChange={(e) => onInputChange('openingTime', e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Clock className="w-4 h-4 inline mr-1" />
                {t('settings.closingTime')}
              </label>
              <input
                type="time"
                value={formData.closingTime}
                onChange={(e) => onInputChange('closingTime', e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors"
              />
            </div>
          </div>

          {/* Operating Hours Preview */}
          {formData.openingTime && formData.closingTime && (
            <div className="mt-6 bg-purple-50 border border-purple-200 rounded-lg p-4">
              <div className="flex items-center">
                <Clock className="w-5 h-5 text-purple-600 mr-3" />
                <span className="text-sm text-purple-700 font-medium">
                  {t('settings.operatingHours')}: {formData.openingTime} - {formData.closingTime}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Subscription Settings */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
          <h4 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Settings className="w-5 h-5" />
            {t('settings.subscriptionSettings')}
          </h4>
          <p className="text-sm text-gray-600 mt-1">{t('settings.subscriptionSettingsDescription')}</p>
        </div>
        
        <div className="p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Users className="w-4 h-4 inline mr-1" />
                {t('settings.minimumDaysBeforeSubscription')}
              </label>
              <input
                type="number"
                min="1"
                value={formData.minimumDaysBeforeSubscriptionCreation}
                onChange={(e) => onInputChange('minimumDaysBeforeSubscriptionCreation', parseInt(e.target.value))}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors"
              />
              <p className="text-xs text-gray-500 mt-1">{t('settings.minimumDaysHelp')}</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Calendar className="w-4 h-4 inline mr-1" />
                {t('settings.mustSelectMealsBeforeDays')}
              </label>
              <input
                type="number"
                min="1"
                value={formData.mustSelectMealsBeforeDays}
                onChange={(e) => onInputChange('mustSelectMealsBeforeDays', parseInt(e.target.value))}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors"
              />
              <p className="text-xs text-gray-500 mt-1">{t('settings.mustSelectMealsHelp')}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
